import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CardContent, Divider, Grid, IconButton, InputAdornment, MenuItem, Modal, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import { useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { globalRemoveMaskReal2 } from "../../../../shared/utils";
import { IComissionData } from "../../types/types";
import { AutoCompleteProposal } from "../AutoCompleteProposalComission";
import { ComissionServices } from "../../../../shared/services/api/comission/ComissionServices";
import { ToastContent } from "../../../../shared/components";

interface IVFormErros {
	[key: string]: string;
}
interface IComissionRegister {
	clientId: string;
	type: string;
	description: string;
	dueDate: string;
	value: number;
	installmentValue: number;
	status: string;
	proposalId: string;
}

interface ModalRegisterComissionProps {
	visible: boolean;
	onClose: () => void;
	rowsInstallment: Partial<IComissionData> & { reference?: string; id: string; clientName?: string };
	onSuccess: () => void;
}

const style = {
	position: "absolute" as const,
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: "40%",
	maxWidth: "75%",
	bgcolor: "background.paper",
	boxShadow: 24,
	borderRadius: "6px",
	p: 3,
};

export const ModalRegisterComission: React.FC<ModalRegisterComissionProps> = ({ visible, onClose, rowsInstallment, onSuccess }) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedProposalId, setSelectedProposalId] = useState<string>();

	const formValidationSchema: yup.SchemaOf<IComissionRegister> = yup.object().shape({
		clientId: yup.string().required("Campo obrigatório"),
		type: yup.string().required("Campo obrigatório"),
		description: yup.string().required("Campo obrigatório"),
		dueDate: yup.string().required("Campo obrigatório"),
		value: yup.number().required("Campo obrigatório"),
		installmentValue: yup.number().required("Campo obrigatório"),
		proposalId: yup.string().required("Campo obrigatório"),
		status: yup.string().required("Campo obrigatório"),
	});

	const handleSave = (data: IComissionRegister) => {
		setIsLoading(true);
		const cleanedData = {
			...data,
			clientId: rowsInstallment.clientId || "",
			type: data.type,
			dueDate: data.dueDate || new Date().toISOString(),
			value: Number(globalRemoveMaskReal2(data.value.toString())),
			installmentValue: Number(globalRemoveMaskReal2(data.value.toString())),
			proposalId: selectedProposalId,
			status: "preview",
		};

		formValidationSchema
			.validate(cleanedData, { abortEarly: false })
			.then((validatedData) => {
				setIsLoading(true);
				console.log("Dados validados:", validatedData);
				ComissionServices.addValues(validatedData).then((result) => {
					setIsLoading(false);
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={"Erro"} content={result.message} />,
						});
					} else {
						toastHandler.present({
							type: "success",
							messageOrContent: "Lançamento efetuado com sucesso!",
							position: "top-right",
						});
						onSuccess();
						onClose();
						setSelectedProposalId(undefined); // reseta garantia selecionada ao fechar
					}
				});
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
		setIsLoading(false);

		formValidationSchema
			.validate(cleanedData, { abortEarly: false })
			.then((validatedData) => {
				setIsLoading(true);
			})
			.catch((errors: yup.ValidationError) => {
				const validationErros: IVFormErros = {};
				errors.inner.forEach((error) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			});
	};

	const handleCloseModal = useCallback(() => {
		onClose();
	}, [onClose]);

	useEffect(() => {
		formRef.current?.setFieldValue("proposalId", selectedProposalId || "");
	}, [selectedProposalId, formRef]);

	return (
		<Modal open={visible} onClose={handleCloseModal}>
			<Box sx={style}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h5" paddingBottom={2} textAlign={"center"} color={"primary"}>
						Lançamento de valores
					</Typography>
					<IconButton onClick={handleCloseModal}>
						<CloseIcon />
					</IconButton>
				</Box>

				<Typography variant="body2" mt={1} mb={1}>
					{rowsInstallment.clientId ? ` ${rowsInstallment.clientName ?? rowsInstallment.clientId}` : "Selecione um cliente"}
				</Typography>
				<Divider sx={{ mb: 2 }} />

				<CardContent sx={{ px: 0 }}>
					<VForm ref={formRef} onSubmit={handleSave} onPointerEnterCapture={""} onPointerLeaveCapture={() => {}} placeholder="">
						<>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<AutoCompleteProposal
										clientId={rowsInstallment.clientId}
										labelSelect="Proposta"
										namecampo="proposalId"
										onChange={(value) => {
											setSelectedProposalId(value?.proposal_id);
										}}
									/>
								</Grid>

								<Grid item xs={12}>
									<VTextField fullWidth label="Descrição" name="description" />
								</Grid>

								<Grid item xs={4}>
									<VTextField fullWidth label="Tipo" name="type" select>
										<MenuItem value="discount">Estorno</MenuItem>
										<MenuItem value="bonus">Bonificação</MenuItem>
										<MenuItem value="parcel">Premiação</MenuItem>
									</VTextField>
								</Grid>

								<Grid item xs={4}>
									<VMaskTextField
										fullWidth
										label="Valor Total"
										name="value"
										mask="real"
										InputProps={{
											startAdornment: <InputAdornment position="start">R$</InputAdornment>,
										}}
									/>
								</Grid>
							</Grid>

							<Divider sx={{ mt: 2 }} />
							<Grid container justifyContent="right" paddingTop={1} gap={2}>
								<Button variant="contained" color="inherit" onClick={handleCloseModal}>
									Cancelar
								</Button>
								<Button variant="contained" onClick={() => formRef.current?.submitForm()} disabled={isLoading || !selectedProposalId}>
									Confirmar
								</Button>
							</Grid>
						</>
					</VForm>
				</CardContent>
			</Box>
		</Modal>
	);
};
